import * as mgrs from 'mgrs';
import type { Coordinate } from 'ol/coordinate';
import { toStringHDMS } from 'ol/coordinate';
import type { ProjectionLike } from 'ol/proj';
import { transform } from 'ol/proj';
import * as proj from 'ol/proj';

import type { NoraCoordinateFormat } from '@nora-monorepo/shared';
import { NoraSupportedCoordinateFormats } from '@nora-monorepo/shared';

import { UTMUtilities } from '#ui-shared/utils/utm';

export function formatLatLongCoordinate(
  latLong: LatLong,
  coordinateFormat: NoraCoordinateFormat,
): string {
  switch (coordinateFormat) {
    case 'WGS84':
      return latLong.asLatLongString();
    case 'DMS':
      return latLong.asLatLongHDMSString();
    case 'HDMM':
      return latLong.asDMMString();
    case 'WEB_MERCATOR':
      return latLong.asWebMercatorString();
    case 'UTM':
      return latLong.asUTMString();
    case 'MGRS':
      return latLong.asMGRSString();
  }
}

/**
 * Represents a Latitude/Longitude position.
 */
export class LatLong {
  readonly latitude: number;
  readonly longitude: number;

  constructor(latitude: number, longitude: number) {
    this.latitude = latitude;
    this.longitude = longitude;
  }

  get lat(): number { return this.latitude; }
  get lon(): number { return this.longitude; }

  static fromWebMercator(coord: Coordinate): LatLong {
    const coords = transform(
      coord,
      NoraSupportedCoordinateFormats.WEB_MERCATOR,
      NoraSupportedCoordinateFormats.WGS84,
    );

    return new LatLong(coords[1], coords[0]);
  }

  static fromProjection(coordinates: Coordinate, coordinateSystem: NoraCoordinateFormat): LatLong {
    const coords = transform(
      coordinates,
      coordinateSystem,
      NoraSupportedCoordinateFormats.WGS84,
    );

    return new LatLong(coords[1], coords[0]);
  }

  /**
   * Converts this LatLong into a plain object
   */
  toJson(): { latitude: number; longitude: number } {
    return {
      latitude: this.latitude,
      longitude: this.longitude,
    };
  }

  /**
   * Converts a plain lat/long data object into a LatLong
   * @param json
   * @param json.latitude
   * @param json.longitude
   */
  static fromJson(json: { latitude: number; longitude: number }): LatLong {
    return new LatLong(json.latitude, json.longitude);
  }

  /**
   * Constructs an OpenLayers Coordinate type from this LatLong
   */
  toOlCoordinate(): Coordinate {
    return [this.longitude, this.latitude];
  }

  /**
   * Returns the coordinates of this LatLong in the given projection.
   * @param targetProjection
   */
  toProjection(targetProjection: ProjectionLike): Coordinate {
    return proj.fromLonLat([this.longitude, this.latitude], targetProjection);
  }

  /**
   * Returns the string representation of this LatLong. String will be rounded to the number of decimals specified by the {@link fixed} parameter (default is 6).
   * @params fixed - the number of decimals to round the latitude and longitude to (default is 6)
   */
  asLatLongString(fixed: number = 6): string {
    return `${this.latitude.toFixed(fixed)}, ${this.longitude.toFixed(fixed)}`;
  }

  /**
   * Returns the HDMS string of this LatLong.
   * HDMS is Hemisphere, Degrees, Minutes and Seconds.
   */
  asLatLongHDMSString(): string {
    return `${toStringHDMS([this.longitude, this.latitude], 1)}`;
  }

  /**
   * Returns the DMM-formated coordinates
   */
  asDMMString(): string {
    const latDMM = LatLong.latLonToDmm(this.latitude, true);
    const lonDMM = LatLong.latLonToDmm(this.longitude, false);
    return `${latDMM}, ${lonDMM}`;
  }

  /**
   * Returns the Web Mercator string representation of this LatLong
   */
  asWebMercatorString(): string {
    const [x, y] = transform([this.longitude, this.latitude], NoraSupportedCoordinateFormats.WGS84, NoraSupportedCoordinateFormats.WEB_MERCATOR);
    const eastingSuffix = x >= 0 ? 'E' : 'W';
    const northingSuffix = y >= 0 ? 'N' : 'S';
    const easting = Math.abs(x).toFixed(1);
    const northing = Math.abs(y).toFixed(1);

    return `${easting}${eastingSuffix}, ${northing}${northingSuffix}`;
  }

  /**
   * Returns a globally valid UTM string representation. Takes into account all UTM zones.
   */
  asUTMString(): string {
    const zone = UTMUtilities.getUTMZone(this.latitude, this.longitude);
    const epsgCode = UTMUtilities.getEPSGCode(zone, this.latitude);
    const [easting, northing] = transform([this.longitude, this.latitude], NoraSupportedCoordinateFormats.WGS84, `EPSG:${epsgCode}`);

    const roundedEasting = Math.round(easting).toString().padStart(6, '0');
    const roundedNorthing = Math.round(northing).toString().padStart(7, '0');
    const letter = UTMUtilities.getUTMLetterDesignator(this.latitude);
    return `${zone}${letter} ${roundedEasting} ${roundedNorthing}`;
  }

  /**
   * Returns this LatLong as an MGRS-formatted string
   */
  asMGRSString(): string {
    const mgrsString = mgrs.forward(([this.longitude, this.latitude]), 5);
    return `${mgrsString}`;
  }

  /**
   * Computes the DMM-format from LatLong
   * @param decimal
   * @param isLat
   */
  private static latLonToDmm(decimal: number, isLat = true): string {
    const direction = isLat
      ? (decimal >= 0 ? 'N' : 'S')
      : (decimal >= 0 ? 'E' : 'W');

    const abs = Math.abs(decimal);
    const degrees = Math.floor(abs);
    const minutes = (abs - degrees) * 60;

    return `${degrees}° ${minutes.toFixed(3)}′ ${direction}`;
  }
}
